import axiosAuthorised, { DEV_OVERRIDE_HEADER } from './AxiosAuthorised';
import { FieldValues } from 'react-hook-form';
import { AxiosResponse } from 'axios';

export enum SendMailSourceEnum {
	Admin = 1,
	ClientSite = 2,
	APIOld = 3,
	API = 4,
	SendsPage = 5,
	ClientSiteNew = 6,
	MobileApp = 7,
}

export type FileResponseType = {
	fileUuid: string;
	fileHash: string;
};

type RequestLargeFileUploadResponseType = {
	code: string;
	name: string;
};

export const maxCloudflareFileSizeInBytes = 100 * 1024 * 1024;
export const maxFileSizeInBytes = 200 * 1024 * 1024;
export const maxPrivilegedFileSizeInBytes = 300 * 1024 * 1024;

export const chunkFileBytesThreshold = 50 * 1024 * 1024;

const chunkLargeFile = async (file: any) => {
	let startPoint = 0;
	const fileChunks: Array<any> = [];

	while (startPoint <= file.size) {
		const newStartPointer = startPoint + chunkFileBytesThreshold;
		fileChunks.push(file.slice(startPoint, newStartPointer));
		startPoint = newStartPointer;
	}

	const largeRequestDetails = await axiosAuthorised
		.get(`${process.env.REACT_APP_FILES_HOST}/client/v1/large-files/temp-file`, {
			...DEV_OVERRIDE_HEADER,
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		})
		.then((r: AxiosResponse<RequestLargeFileUploadResponseType>) => r.data);

	const successChunks: Array<boolean> = [];

	for (let index = 0; index < fileChunks.length; index++) {
		const fileData = new FormData();

		fileData.append('code', largeRequestDetails.code);
		fileData.append('totalSize', file.size);
		fileData.append('partitionCount', fileChunks.length.toString());
		fileData.append('partitionPart', (index + 1).toString());
		fileData.append('file', fileChunks[index]);

		await axiosAuthorised
			.put(
				`${process.env.REACT_APP_FILES_HOST}/client/v1/large-files/${largeRequestDetails.name}/chunk`,
				fileData,
				{
					...DEV_OVERRIDE_HEADER,
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				}
			)
			.then((r) => successChunks.push(r.status === 201))
			.catch((err: Error) => console.log('err', err));
	}

	if (successChunks.every((success) => success)) {
		const fileData = new FormData();

		fileData.append('code', largeRequestDetails.code);
		fileData.append('fileName', largeRequestDetails.name);
		fileData.append('PageCount', '1');
		fileData.append('FileCategory', '4');

		const finalResp = await axiosAuthorised.put(
			`${process.env.REACT_APP_FILES_HOST}/client/v1/large-files/${largeRequestDetails.name}`,
			fileData,
			{
				...DEV_OVERRIDE_HEADER,
				headers: {
					'Content-Type': 'multipart/form-data',
				},
			}
		);

		return finalResp.data;
	} else {
		return {
			error: 'An error occurred while uploading your file',
		};
	}
};

const uploadFilesForMerge = async (files: any) => {
	const fileData = new FormData();

	files.forEach((file: any) => {
		fileData.append('files', file);
		fileData.append('fileName', file.name);
		fileData.append('PageCount', '1');
		fileData.append('FileCategory', '4');
	});

	const resp = await axiosAuthorised.post(
		`${process.env.REACT_APP_FILES_HOST}/client/v1/files/merge-upload`,
		fileData,
		{
			...DEV_OVERRIDE_HEADER,
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		}
	);

	return resp.data;
};

const uploadFile = async (file: any) => {
	if (file.size > maxCloudflareFileSizeInBytes) return await chunkLargeFile(file);

	const fileData = new FormData();

	fileData.append('file', file);
	fileData.append('fileName', file.name);
	fileData.append('PageCount', '1');
	fileData.append('FileCategory', '4');

	const resp = await axiosAuthorised.post(`${process.env.REACT_APP_FILES_HOST}/client/v1/userfile`, fileData, {
		...DEV_OVERRIDE_HEADER,
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
	});

	return resp.data;
};

const sendMail = async (props: FieldValues) => {
	let postData: any = {
		fileUuid: props.file,
		notes: props.notes ?? '',
		clientReferenceId: props.clientReferenceId ?? '',
		returnAddress: '',
		pageCount: 1,
		postageOptionId: props.postage,
		mailSourceType: SendMailSourceEnum.SendsPage,
		addressId: props.address,
		sendsPageId: props.sendsPageId,
	};

	//return Promise.reject(new Error('fail'));

	const { data } = await axiosAuthorised.post(
		`${process.env.REACT_APP_API_HOST}/client/v1/mail/send`,
		JSON.stringify(postData),
		DEV_OVERRIDE_HEADER
	);

	return data;
};

export { sendMail, uploadFilesForMerge, uploadFile };
